// ============================================================
//  src/api/client.js
// ============================================================
import axios from 'axios';

// ── Axios Instance ─────────────────────────────────────────
const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ── Date Token (YYYYMMDD) for /account/* endpoints ─────────
export const getDateToken = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
};

// ── Request Interceptor ────────────────────────────────────
client.interceptors.request.use((config) => {
  if (!config.headers['Authorization']) {
    const token = localStorage.getItem('authToken');
    if (token) config.headers['Authorization'] = token;
  }
  return config;
});

// ── Public paths that must never trigger the /login redirect
//    (no user auth required — 401 on these is not a session issue)
const PUBLIC_PATHS = [
  '/app/settings/',
  '/account/login/',
  '/account/register/',
  '/account/recover/',
  '/account/verify-code/',
  '/account/update-password/',
];

// ── Response Interceptor ───────────────────────────────────
client.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err.config?.url || '';
    const isPublic = PUBLIC_PATHS.some((p) => url.includes(p));

    if (err.response?.status === 401 && !isPublic) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  },
);

// ── Account ────────────────────────────────────────────────
export const postRegister = (data) =>
  client.post('/account/register/', data, { headers: { Authorization: `Token ${getDateToken()}` } });

export const postLogin = (data) =>
  client.post('/account/login/', data, { headers: { Authorization: `Token ${getDateToken()}` } });

export const postRecoverPassword = (data) =>
  client.post('/account/recover/', data, { headers: { Authorization: `Token ${getDateToken()}` } });

export const postVerifyCode = (data) =>
  client.post('/account/verify-code/', data, { headers: { Authorization: `Token ${getDateToken()}` } });

export const postUpdatePassword = (data) =>
  client.post('/account/update-password/', data, { headers: { Authorization: `Token ${getDateToken()}` } });

// ── Email Verification ─────────────────────────────────────
export const postResendVerification = () => client.post('/user/resend-verification/', {});
export const postVerifyEmail = (data) => client.post('/user/verify-email/', data);

// ── User Profile ───────────────────────────────────────────
export const getUser = () => client.post('/user/', {});
export const getTransactions = (params = {}) => client.post('/user/transactions/', params);
export const getTransactionDetails = (data) => client.post('/user/transaction-details/', data);
export const getDailyLimit = () => client.post('/user/daily-limit/', {});
export const getAccountTier = () => client.post('/user/tier/', {});
export const getTierRequirements = () => client.post('/user/tier-requirements/', {});
export const postRequestUpgrade = (data) => client.post('/user/request-upgrade/', data);
export const postDeleteAccount = () => client.post('/user/delete-account/', {});

// Profile Picture
export const postProfilePicture = (formData) =>
  formData
    ? client.post('/user/profile-picture/', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    : client.post('/user/profile-picture/', {});
export const deleteProfilePicture = () => client.post('/user/profile-picture/delete/', {});

// Funding
export const getFundingAccounts = () => client.post('/user/funding-accounts/', {});
export const postGenerateAccount = () => client.post('/user/generate-account/', {});

// Submit proof of address
export const postSubmitProofOfAddress = (formData) =>
  client.post('/user/submit-proof-of-address/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Wallets
export const getReferralStats = () => client.post('/user/referral-stats/', {});
export const postReferralToWallet = (data) => client.post('/user/referral-to-wallet/', data);
export const postCashbackToWallet = (data) => client.post('/user/cashback-to-wallet/', data);
export const postWalletTransfer = (data) => client.post('/user/wallet-transfer/', data);

// ── Beneficiaries ──────────────────────────────────────────
export const getBeneficiaries = () => client.post('/user/beneficiaries/', {});
export const postAddBeneficiary = (data) => client.post('/user/beneficiary/add/', data);
export const postDeleteBeneficiary = (data) => client.post('/user/beneficiary/delete/', data);

// ── KYC ───────────────────────────────────────────────────
export const postVerifyBvn = (data) => client.post('/user/verify-bvn/', data);
export const postVerifyNin = (data) => client.post('/user/verify-nin/', data);

// ── PIN ────────────────────────────────────────────────────
export const postRequestPinCode = () => client.post('/user/request-pin-code/', {});
export const postUpdatePin = (data) => client.post('/user/update-pin/', data);

// ── Support ────────────────────────────────────────────────
export const postSupportTicket = (data) => client.post('/support/', data);
export const getSupportTickets = () => client.post('/support/tickets/', {});
export const getSupportTicketView = (data) => client.post('/support/view/', data);
export const postSupportReply = (data) => client.post('/support/reply/', data);
export const getSupportUnread = () => client.post('/support/unread/', {});

// ── App Info ───────────────────────────────────────────────
export const getContactDetails = () => client.post('/contact/', {});
export const getNotifications = () => client.post('/notifications/', {});
export const getAds = () => client.post('/ads/', {});
export const getAppSettings = () => client.post('/app/settings/', {});

// ── Service Plans ──────────────────────────────────────────
export const getNetworks = () => client.post('/networks/', {});
export const getDataPlans = () => client.post('/data/plans/', {});
export const getCablePlans = (data) => client.post('/cabletv/plans/', data);
export const getElectricityProviders = () => client.post('/electricity/providers/', {});
export const getExamProviders = () => client.post('/exam/providers/', {});

// ── Purchases ──────────────────────────────────────────────
export const postBuyAirtime = (data) => client.post('/airtime/', data);
export const postBuyData = (data) => client.post('/data/', data);
export const postVerifySmartCard = (data) => client.post('/cabletv/verify/', data);
export const postBuyCableTV = (data) => client.post('/cabletv/', data);
export const postVerifyMeter = (data) => client.post('/electricity/verify/', data);
export const postBuyElectricity = (data) => client.post('/electricity/', data);
export const postBuyExamPin = (data) => client.post('/exam/', data);

export default client;